import React from 'react'
import { Plus } from 'lucide-react'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'
import { CheckBox } from '@modules/shadcn/ui/CheckBox/CheckBox'
import { InfoTooltip } from '@modules/shadcn/ui/Tooltip/Tooltip'

import type { SimulationPermissions } from '@ai-color-modules/permissions'
import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import type { MaterialOption } from '@ai-color-modules/simulation/MaterialPalette/materialOptions'
import type { ExcludeArea, SubArea } from '@ai-color-modules/simulation/types'

import { ExecutionPanel } from './components/ExecutionPanel'
import { LayerInlineMenu } from './components/LayerInlineMenu'
import { LayerItem } from './components/LayerItem'
import { OptionPanel } from './components/OptionPanel'
import {
  CUSTOM_AREA_DETECTED_ONLY_DESCRIPTION,
  CUSTOM_AREA_DETECTED_ONLY_LABEL,
  CUSTOM_AREA_DETECTED_ONLY_TOOLTIP,
} from './constants'
import type { MaskPollingPhase } from './hooks/useMaskPolling'

interface AreaColorSelectorProps {
  // 権限
  permissions?: SimulationPermissions

  // ベースエリア関連
  baseAreas: string[]
  areaColors: Record<string, string>
  colorOptions: ColorOption[]
  /** ベースエリア（屋根・外壁）に選択された素材ID */
  areaMaterials?: Record<string, bigint>
  /** ベースエリアごとの「色 / 素材どちらが現在 active か」 */
  areaSelectionMode?: Record<string, 'color' | 'material'>
  /** 素材マスター（id → サムネ・名称解決用） */
  materialOptions?: MaterialOption[]
  /** 屋根・外壁ベースエリアでの素材選択を許可するか（false 時は色のみ） */
  canEditMaterial?: boolean
  onBaseAreaColorClick: (area: string) => void
  onBaseAreaResetClick: (area: string) => void
  onBaseAreaAdjustClick?: (area: string) => void
  /**
   * マスク再生成の回数上限（決定 57 / 79 C6）に達したベースエリア名。
   * 該当する部位のインラインメニューから「エリアを再生成」を無効表示にする
   * （平常時は空配列で、通常の表示は一切変わらない）。
   */
  maskRegenerationLimitedAreas?: string[]

  // オプション関連
  unifyColor: boolean
  onUnifyColorChange: (checked: boolean) => void

  // レイヤーUI関連
  openLayerId: string | null
  onOpenLayerChange: React.Dispatch<React.SetStateAction<string | null>>
  onColorPaletteClose: () => void
  onCloseColorPaletteOnly: () => void

  // サブエリア関連
  isSubAreaModeActive: boolean
  selectedSubAreaIndex: number
  onSubAreaModeToggle?: () => void
  subAreas: SubArea[]
  onSubAreaSelectClick?: (index: number) => void
  onSubAreaColorClick?: (index: number) => void
  onSubAreaResetClick?: (index: number) => void
  onSubAreaSettingClick?: (index: number) => void
  onSubAreaDeleteClick?: (index: number) => void
  onSubAreaAddClick?: () => void
  /**
   * カスタムエリアの有効範囲設定（`SubArea.useDetectedAreaOnly`）のトグル（issue #2413）。
   * 設定ダイアログと同じ値を一覧から直接切り替えるためのもの。
   * 未指定なら一覧にチェックボックスを出さない（= 設定権限のない施主側）。
   */
  onSubAreaUseDetectedAreaOnlyChange?: (index: number, useDetectedAreaOnly: boolean) => void

  // 除外エリア関連
  isExcludeAreaModeActive: boolean
  excludeAreas: ExcludeArea[]
  selectedExcludeIdx: number
  onExcludeAreaSelectClick?: (index: number) => void
  onExcludeAreaDeleteClick?: (index: number) => void
  onExcludeAreaAddClick?: () => void
  onExcludeAreaModeToggle?: () => void

  // 実行ボタン関連
  canProcessImage: boolean
  processing: boolean
  onProcessClick: () => void

  // マスクポーリング状態
  maskPollingPhase?: MaskPollingPhase
  isMaskRegenerating?: boolean
}

/**
 * エリア色選択サイドバーコンポーネント
 * ベースエリア、カスタムエリア、除外エリアの設定UIを提供
 */
export const AreaColorSelector: React.FC<AreaColorSelectorProps> = props => {
  const {
    permissions,
    baseAreas,
    areaColors,
    colorOptions,
    areaMaterials,
    areaSelectionMode,
    materialOptions,
    canEditMaterial = false,
    onBaseAreaColorClick,
    onBaseAreaResetClick,
    onBaseAreaAdjustClick,
    maskRegenerationLimitedAreas = [],
    unifyColor,
    onUnifyColorChange,
    openLayerId,
    onOpenLayerChange,
    onColorPaletteClose,
    onCloseColorPaletteOnly,
    isSubAreaModeActive,
    selectedSubAreaIndex,
    onSubAreaModeToggle,
    subAreas,
    onSubAreaSelectClick,
    onSubAreaColorClick,
    onSubAreaResetClick,
    onSubAreaSettingClick,
    onSubAreaDeleteClick,
    onSubAreaAddClick,
    onSubAreaUseDetectedAreaOnlyChange,
    isExcludeAreaModeActive,
    excludeAreas,
    selectedExcludeIdx,
    onExcludeAreaSelectClick,
    onExcludeAreaDeleteClick,
    onExcludeAreaAddClick,
    onExcludeAreaModeToggle,
    canProcessImage,
    processing,
    onProcessClick,
    maskPollingPhase,
    isMaskRegenerating = false,
  } = props

  const selectedLayerId = isExcludeAreaModeActive
    ? excludeAreas.length > 0
      ? `exclude-${selectedExcludeIdx}`
      : null
    : isSubAreaModeActive
      ? subAreas.length > 0
        ? `custom-${selectedSubAreaIndex}`
        : null
      : null

  const isMaskPreparing =
    isMaskRegenerating || maskPollingPhase === 'initial' || maskPollingPhase === 'polling'

  const buttonLabel = () => {
    if (isMaskRegenerating) {
      return 'エリアを再生成中...'
    }
    if (isMaskPreparing) {
      return 'エリアを準備中...'
    }
    if (maskPollingPhase === 'timeout') {
      return '準備に時間がかかっています'
    }
    if (processing) return '処理中...'
    if (!canProcessImage) return '変更色の指定が必要です'
    return 'この設定で実行'
  }

  const getLayerState = (layerId: string) => {
    if (openLayerId === layerId || selectedLayerId === layerId) {
      return 'selected'
    }
    return 'default'
  }

  const handleBaseLayerSelect = () => {
    if (isSubAreaModeActive) {
      onSubAreaModeToggle?.()
    }
    if (isExcludeAreaModeActive) {
      onExcludeAreaModeToggle?.()
    }
  }

  const handleSubAreaSelect = (index: number) => {
    if (!isSubAreaModeActive) {
      onSubAreaModeToggle?.()
    }
    if (isExcludeAreaModeActive) {
      onExcludeAreaModeToggle?.()
    }
    onSubAreaSelectClick?.(index)
  }

  const handleExcludeAreaSelect = (index: number) => {
    if (!isExcludeAreaModeActive) {
      onExcludeAreaModeToggle?.()
    }
    if (isSubAreaModeActive) {
      onSubAreaModeToggle?.()
    }
    onExcludeAreaSelectClick?.(index)
  }

  /** レイヤークリック: フォーカス + モード切替 + パレット自動オープン
   *  既に選択中のレイヤーをクリックした場合は非選択化する */
  const handleLayerClick = (layerId: string, onSelect: () => void, onColorOpen?: () => void) => {
    const isAlreadySelected = openLayerId === layerId || selectedLayerId === layerId
    if (isAlreadySelected) {
      onColorPaletteClose()
      if (layerId.startsWith('custom-') && isSubAreaModeActive) {
        onSubAreaModeToggle?.()
      }
      if (layerId.startsWith('exclude-') && isExcludeAreaModeActive) {
        onExcludeAreaModeToggle?.()
      }
      return
    }
    onOpenLayerChange(layerId)
    onSelect()
    onColorOpen?.()
  }

  /**
   * 屋根・外壁ベースエリアで素材選択を許可するか.
   * 雨樋・破風には素材選択を出さない（Issue #1785 1st リリース）.
   */
  const isMaterialEligibleArea = (area: string): boolean =>
    canEditMaterial && (area === '外壁' || area === '屋根')

  /** ベースエリア向けの「色」「素材」「色/素材」ラベル. */
  const getAreaActionLabel = (area: string, kind: 'select' | 'unset'): string => {
    if (isMaterialEligibleArea(area)) {
      return kind === 'select' ? '色・素材指定' : '色・素材を解除'
    }
    return kind === 'select' ? '色指定' : '色指定を解除'
  }

  /**
   * ベースエリアの右端バッジに「色」「素材」「未指定」のどれを描画するか決定する.
   * mode が明示的に 'material' / 'color' なら従う。
   * 未設定なら、素材だけ set なら 'material'、それ以外は 'color' (色 or 未指定どちらでも色枠表示).
   */
  const getBadgeDisplayKind = (area: string): 'color' | 'material' => {
    const explicitMode = areaSelectionMode?.[area]
    if (explicitMode === 'material') return 'material'
    if (explicitMode === 'color') return 'color'
    const colorValue = areaColors[area]
    const materialId = areaMaterials?.[area]
    return materialId !== undefined && !colorValue ? 'material' : 'color'
  }

  const renderColorShortcutButton = ({
    label,
    colorValue,
    materialId,
    badgeVariant = 'pill',
    disabled = false,
    onClick,
  }: {
    label: string
    colorValue?: string
    materialId?: bigint
    badgeVariant?: 'pill' | 'flat'
    disabled?: boolean
    onClick: () => void
  }) => {
    const colorOption = colorValue
      ? colorOptions.find(option => option.value === colorValue)
      : undefined
    const materialOption =
      materialId !== undefined
        ? materialOptions?.find(option => option.id === materialId)
        : undefined

    const ariaLabel = isMaterialEligibleArea(label)
      ? `${label}の色・素材を選択`
      : `${label}の色を選択`
    const emptyLabel = isMaterialEligibleArea(label) ? '未指定' : '色指定なし'

    const renderMaterialBadge = () => {
      if (!materialOption) return null
      return (
        <span
          className={cn(
            'flex items-center gap-2',
            badgeVariant === 'pill' && 'rounded bg-white/70 px-2 py-1',
          )}
        >
          <span
            aria-hidden
            className="h-3.5 w-3.5 overflow-hidden rounded border border-white/50 bg-cover bg-center"
            style={{ backgroundImage: `url(${materialOption.thumbnailUrl})` }}
          />
          <span className="max-w-[5em] truncate text-xs text-[#18181B]">
            {materialOption.colorName}
          </span>
        </span>
      )
    }

    const renderColorBadge = () => {
      if (!colorOption) return null
      return badgeVariant === 'flat' ? (
        <span className="flex items-center gap-1">
          <span
            aria-hidden
            className="h-3.5 w-3.5 rounded-[4px] border border-white/50"
            style={{ backgroundColor: colorOption.color }}
          />
          <span className="max-w-[5em] truncate text-xs text-[#18181B]">{colorOption.value}</span>
        </span>
      ) : (
        <span className="flex items-center gap-2 rounded bg-white/70 px-2 py-1">
          <span
            aria-hidden
            className="h-3.5 w-3.5 rounded border border-white/50"
            style={{ backgroundColor: colorOption.color }}
          />
          <span className="max-w-[5em] truncate text-xs text-[#18181B]">{colorOption.value}</span>
        </span>
      )
    }

    return (
      <button
        aria-label={ariaLabel}
        className={cn(
          'absolute right-3 top-1/2 z-10 -translate-y-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        disabled={disabled}
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          onClick()
        }}
        onPointerDown={event => {
          event.preventDefault()
          event.stopPropagation()
        }}
        type="button"
      >
        {materialOption ? (
          renderMaterialBadge()
        ) : colorOption ? (
          renderColorBadge()
        ) : (
          <span className="text-[10px] text-[#71717A]">{emptyLabel}</span>
        )}
      </button>
    )
  }

  return (
    <aside className="flex h-full w-full flex-col overflow-hidden rounded-md border border-[#E4E4E7] bg-white lg:min-h-[638px] lg:w-60 lg:shrink-0 lg:self-stretch">
      <div className="flex flex-1 flex-col">
        <section className="space-y-2 border-b border-[#E4E4E7] p-3">
          <h2 className="text-xs font-bold tracking-[-0.02em] text-[#09090B]">ベースエリア</h2>
          {isMaskPreparing ? (
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-3">
              <div className="flex items-start gap-2">
                <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#10B981]" />
                <div className="space-y-1">
                  <p className="text-sm font-medium tracking-[-0.025em] text-[#09090B]">
                    {isMaskRegenerating ? 'エリアを再生成中...' : 'ベースエリアをAIが判定中...'}
                  </p>
                  <p className="text-xs text-[#71717A]">
                    {isMaskRegenerating
                      ? '反映まで少しお待ちください'
                      : 'もう少しだけお待ちください'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          {baseAreas.length === 0 ? (
            // 施主側でこのカット（外観カット）に編集可能なベースエリアが 1 つも無いケース。
            // 企業側で色変更されていないカットは施主が触れない仕様なので、理由を明示する。
            <p className="text-xs text-slate-500">
              この画像は企業側で色変更されていないため変更できません
            </p>
          ) : null}
          <div className="space-y-2">
            {baseAreas.map(area => {
              const layerId = `base-${area}`
              return (
                <div key={layerId} className={cn('relative', openLayerId === layerId && 'z-20')}>
                  <div className="relative">
                    <LayerItem
                      aria-expanded={openLayerId === layerId}
                      className="pr-[76px]"
                      label={area}
                      onClick={() =>
                        handleLayerClick(layerId, handleBaseLayerSelect, () =>
                          onBaseAreaColorClick(area),
                        )
                      }
                      showColor={false}
                      state={getLayerState(layerId)}
                    />
                    {renderColorShortcutButton({
                      label: area,
                      colorValue:
                        getBadgeDisplayKind(area) === 'color' ? areaColors[area] : undefined,
                      materialId:
                        getBadgeDisplayKind(area) === 'material'
                          ? areaMaterials?.[area]
                          : undefined,
                      onClick: () =>
                        handleLayerClick(layerId, handleBaseLayerSelect, () =>
                          onBaseAreaColorClick(area),
                        ),
                    })}
                  </div>
                  {openLayerId === layerId && (
                    <LayerInlineMenu
                      actions={[
                        {
                          label: getAreaActionLabel(area, 'select'),
                          onClick: () => {
                            onOpenLayerChange(layerId)
                            onBaseAreaColorClick(area)
                          },
                        },
                        ...(onBaseAreaAdjustClick
                          ? [
                              {
                                label: 'エリアを再生成',
                                onClick: () => onBaseAreaAdjustClick(area),
                                disabled: maskRegenerationLimitedAreas.includes(area),
                              },
                            ]
                          : []),
                        ...(areaColors[area] || areaMaterials?.[area] !== undefined
                          ? [
                              {
                                label: getAreaActionLabel(area, 'unset'),
                                onClick: () => onBaseAreaResetClick(area),
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* カスタムエリアセクション:
            - 編集権限あり: 見出し + リスト + 追加ボタン
            - 編集権限なし（施主側）: カスタムエリアがあればリストのみ表示（見出し・追加ボタンなし） */}
        {permissions?.canEditCustomArea !== false ? (
          <section className="space-y-2 border-b border-[#E4E4E7] p-3">
            <h2 className="text-xs font-bold tracking-[-0.02em] text-[#09090B]">カスタムエリア</h2>
            {subAreas.length > 0 ? (
              <div className="space-y-2">
                {subAreas.map((area, index) => {
                  const layerId = `custom-${index}`
                  const isSelected = getLayerState(layerId) === 'selected'
                  // 有効範囲チェックボックスを出すのは企業側のみ（issue #2413）。
                  // ハンドラ未指定 = 権限なし。その場合は従来どおり 1 行表示に戻す。
                  const showDetectedOnlyToggle = onSubAreaUseDetectedAreaOnlyChange !== undefined
                  return (
                    <div
                      key={layerId}
                      className={cn('relative', openLayerId === layerId && 'z-20')}
                    >
                      {/* issue #2413: 有効範囲設定を階層下のダイアログから一覧へ出すため、
                          1 行の LayerItem からカードへ拡張する。選択スタイル・ホバーは外枠側が持ち、
                          内側の LayerItem は枠・背景を透過させてカードに溶け込ませる。 */}
                      <div
                        className={cn(
                          showDetectedOnlyToggle && 'rounded-md border',
                          showDetectedOnlyToggle &&
                            (isSelected
                              ? 'border-emerald-400 bg-[#1BD99A]'
                              : 'border-[#E4E4E7] bg-white hover:bg-[#F4F4F5]'),
                        )}
                      >
                        <div className="relative">
                          <LayerItem
                            aria-expanded={openLayerId === layerId}
                            badgeVariant="flat"
                            className={cn(
                              'pr-[76px]',
                              // hover 変種は twMerge が base の bg-/border- とは別グループとして扱うため、
                              // bg-transparent だけでは LayerItem 側の hover:bg-[#F4F4F5] を打ち消せない。
                              // 明示的に hover も透過させないと、カード上部 1 行だけが灰色化して分断されて見える。
                              showDetectedOnlyToggle &&
                                'border-transparent bg-transparent hover:border-transparent hover:bg-transparent',
                            )}
                            label={area.label ?? `カスタムエリア${index + 1}`}
                            onClick={() =>
                              handleLayerClick(
                                layerId,
                                () => handleSubAreaSelect(index),
                                () => onSubAreaColorClick?.(index),
                              )
                            }
                            showColor={false}
                            state={getLayerState(layerId)}
                          />
                          {renderColorShortcutButton({
                            label: area.label ?? `カスタムエリア${index + 1}`,
                            colorValue: area.color,
                            badgeVariant: 'flat',
                            onClick: () =>
                              handleLayerClick(
                                layerId,
                                () => handleSubAreaSelect(index),
                                () => onSubAreaColorClick?.(index),
                              ),
                          })}
                        </div>
                        {showDetectedOnlyToggle && (
                          <div className="space-y-1 px-4 pb-3">
                            <div className="flex items-center gap-2">
                              <CheckBox
                                aria-describedby={`${layerId}-detected-only-description`}
                                // 既定 ON は生成時（useSubAreaSelection）と復元時（useFormParametersSync）で
                                // 正規化済み。`?? true` は proto / API 側が値を落とした場合の保険。
                                checked={area.useDetectedAreaOnly ?? true}
                                id={`${layerId}-detected-only`}
                                onCheckedChange={(checked: boolean) =>
                                  onSubAreaUseDetectedAreaOnlyChange(index, checked === true)
                                }
                              />
                              <label
                                className="cursor-pointer text-xs font-medium text-[#09090B]"
                                htmlFor={`${layerId}-detected-only`}
                              >
                                {CUSTOM_AREA_DETECTED_ONLY_LABEL}
                              </label>
                              <InfoTooltip
                                className="text-[#3F3F46]"
                                content={CUSTOM_AREA_DETECTED_ONLY_TOOLTIP}
                              />
                            </div>
                            {/* #3F3F46: 選択中カード（bg-[#1BD99A]）上でもコントラスト比 5.69:1 で
                                WCAG AA (4.5:1) を満たす。#52525B は 4.21:1 で未達、
                                既存の副次テキスト色 #71717A は 2.63:1 でさらに低い。 */}
                            <p
                              className="pl-6 text-[10px] leading-snug text-[#3F3F46]"
                              id={`${layerId}-detected-only-description`}
                            >
                              {CUSTOM_AREA_DETECTED_ONLY_DESCRIPTION}
                            </p>
                          </div>
                        )}
                      </div>
                      {openLayerId === layerId && (
                        <LayerInlineMenu
                          actions={[
                            {
                              label: '色指定',
                              onClick: () => {
                                onOpenLayerChange(layerId)
                                onSubAreaColorClick?.(index)
                              },
                            },
                            ...(onSubAreaResetClick
                              ? [
                                  {
                                    label: '色指定を解除',
                                    onClick: () => onSubAreaResetClick(index),
                                  },
                                ]
                              : []),
                            ...(onSubAreaSettingClick
                              ? [
                                  {
                                    label: '設定',
                                    onClick: () => {
                                      onCloseColorPaletteOnly()
                                      onSubAreaSettingClick(index)
                                    },
                                  },
                                ]
                              : []),
                            ...(onSubAreaDeleteClick
                              ? [
                                  {
                                    label: '削除',
                                    className: 'text-red-600',
                                    onClick: () => {
                                      onColorPaletteClose()
                                      onSubAreaDeleteClick(index)
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-500">カスタムエリアはありません</p>
            )}
            {onSubAreaAddClick ? (
              <Button
                aria-label="カスタムエリア追加"
                className="h-8 w-full justify-center gap-2 rounded-md bg-white text-xs font-medium text-[#09090B] hover:bg-[#F4F4F5]"
                onClick={() => {
                  onCloseColorPaletteOnly()
                  onSubAreaAddClick()
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Plus className="h-4 w-4" />
                追加
              </Button>
            ) : null}
          </section>
        ) : subAreas.length > 0 ? (
          /* 施主側: カスタムエリアがあれば表示（見出しなし、ポリゴン編集不可、色指定メニューあり） */
          <div className="space-y-2 border-b border-[#E4E4E7] p-3">
            {subAreas.map((area, index) => {
              const layerId = `custom-${index}`
              return (
                <div key={layerId} className={cn('relative', openLayerId === layerId && 'z-20')}>
                  <div className="relative">
                    <LayerItem
                      aria-expanded={openLayerId === layerId}
                      badgeVariant="flat"
                      className="pr-[76px]"
                      label={area.label ?? `カスタムエリア${index + 1}`}
                      onClick={() =>
                        handleLayerClick(
                          layerId,
                          () => handleSubAreaSelect(index),
                          () => onSubAreaColorClick?.(index),
                        )
                      }
                      showColor={false}
                      state={getLayerState(layerId)}
                    />
                    {renderColorShortcutButton({
                      label: area.label ?? `カスタムエリア${index + 1}`,
                      colorValue: area.color,
                      badgeVariant: 'flat',
                      onClick: () =>
                        handleLayerClick(
                          layerId,
                          () => handleSubAreaSelect(index),
                          () => onSubAreaColorClick?.(index),
                        ),
                    })}
                  </div>
                  {openLayerId === layerId && (
                    <LayerInlineMenu
                      actions={[
                        {
                          label: '色指定',
                          onClick: () => {
                            onOpenLayerChange(layerId)
                            onSubAreaColorClick?.(index)
                          },
                        },
                        ...(onSubAreaResetClick && area.color
                          ? [
                              {
                                label: '色指定を解除',
                                onClick: () => onSubAreaResetClick(index),
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ) : null}

        {permissions?.canEditExcludeArea !== false ? (
          <section className="space-y-2 border-b border-[#E4E4E7] p-3">
            <h2 className="text-xs font-bold tracking-[-0.02em] text-[#09090B]">除外エリア</h2>
            {excludeAreas.length > 0 ? (
              <div className="space-y-2">
                {excludeAreas.map((_, index) => {
                  const layerId = `exclude-${index}`
                  return (
                    <div
                      key={layerId}
                      className={cn('relative', openLayerId === layerId && 'z-20')}
                    >
                      <LayerItem
                        aria-expanded={openLayerId === layerId}
                        label={`除外エリア${index + 1}`}
                        onClick={() =>
                          handleLayerClick(layerId, () => handleExcludeAreaSelect(index))
                        }
                        showColor={false}
                        state={getLayerState(layerId)}
                      />
                      {openLayerId === layerId && onExcludeAreaDeleteClick && (
                        <LayerInlineMenu
                          actions={[
                            {
                              label: '削除',
                              className: 'text-red-600',
                              onClick: () => {
                                onColorPaletteClose()
                                onExcludeAreaDeleteClick(index)
                              },
                            },
                          ]}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-500">除外エリアはありません</p>
            )}
            {onExcludeAreaAddClick ? (
              <Button
                aria-label="除外エリア追加"
                className="h-8 w-full justify-center gap-2 rounded-md bg-white text-xs font-medium text-[#09090B] hover:bg-[#F4F4F5]"
                onClick={() => {
                  onCloseColorPaletteOnly()
                  onExcludeAreaAddClick()
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Plus className="h-4 w-4" />
                追加
              </Button>
            ) : null}
          </section>
        ) : null}
      </div>
      <div className="border-b border-[#E4E4E7] p-3">
        <OptionPanel onUnifyColorChange={onUnifyColorChange} unifyColor={unifyColor} />
      </div>
      <div className={cn('p-3', !canProcessImage && 'bg-slate-50')}>
        <ExecutionPanel
          disabled={!canProcessImage || isMaskPreparing}
          label={buttonLabel()}
          message={
            !canProcessImage && !processing && !isMaskPreparing
              ? 'AIカラーシミュレーション実行には変更色の指定が必要です'
              : undefined
          }
          onClick={onProcessClick}
        />
        {maskPollingPhase === 'timeout' ? (
          <div className="mt-3 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3">
            <p className="text-center text-sm text-slate-700">準備に時間がかかっています</p>
            <p className="mt-1 text-center text-xs text-slate-600">
              そのまま実行するとエリア生成を待機します
            </p>
          </div>
        ) : null}
      </div>
    </aside>
  )
}
